import {
  childServiceFilterMethods,
  type ChildServiceFilterItem,
  beachTypeFilterMethods,
  type BeachTypeFilterItem,
  restTypeFilterMethods,
  type RestTypeFilterItem,
  renovationFilterMethods,
} from '@features/hotel/filterHotels';
import { filterAttributeHotelMethodsByAll } from '@fr/vue/components/filters/common/filterMethods/filterAttributeMethodsHotelBoolean/FilterAttributeHotelMethodsByAll';
import { type IFilterFacility } from '@fr/vue/pages/serp/components/filters/facilitiesFilter/common';
import facilitiesFilterAttributeMethods from '@fr/vue/pages/serp/components/filters/facilitiesFilter/common/FacilitiesFilterAttributeMethods';
import FilterAttributeBeachLineMethods from '@ve/components/filters/beachLineFilter/filterMethods/FilterAttributeBeachLineMethods';
import {
  type IBeachLine,
  type IAttributeHotel,
  type IReviewRating,
  type IFilterCriteriaModel,
  type IWaterActivities,
} from '@ve/components/filters/common/types';
import DistanceLiftAttribute from '@ve/components/filters/distanceLiftFilter/filtersMethods/FilterAttributeDistanceLiftMethods';
import FilterAttributeRatingReviewsMethods from '@ve/components/filters/ratingReviewsFilter/filterMethods/FilterAttributeRatingReviewsMethods';
import filterAttributeMethodsWaterActivities from '@ve/pages/serp/components/filters/filterWaterActivities/common/FilterMethodsWaterActivities';

type OldFilterCriteria =
  | IBeachLine['key']
  | IAttributeHotel['key']
  | IReviewRating['key']
  | BeachTypeFilterItem['key']
  | IWaterActivities['key']
  | IFilterFacility['key']
  | ChildServiceFilterItem['key']
  | RestTypeFilterItem['key'];

type NewFilterCriteria = Pick<
  IFilterCriteriaModel,
  | 'takenHotelAttributes'
  | 'typeLines'
  | 'typeFilterRating'
  | 'takenBeachType'
  | 'takenWaterActivities'
  | 'takenFacilities'
  | 'childServiceFilterIds'
  | 'typeDistanceLift'
  | 'restType'
  | 'renovations'
>;

//функция принимает старый интерфейс фильтров и преобразовывает в новый интерфейс
export function getNewFilterCriteria(
  filterCriteria: Record<OldFilterCriteria, boolean>,
  returnType: 'part' | 'all' = 'all',
): Partial<NewFilterCriteria> {
  if (!filterCriteria) return {} as NewFilterCriteria;
  const idsAttributeHotel = filterAttributeHotelMethodsByAll.getIdsAttributesByObjectKeys(filterCriteria);
  const idsBeachLine = FilterAttributeBeachLineMethods.getIdsAttributesByObjectKeys(filterCriteria);
  const idsRatingReviews = FilterAttributeRatingReviewsMethods.getIdsAttributesByObjectKeys(filterCriteria);
  const idsBeachType = beachTypeFilterMethods.getIdsAttributesByObjectKeys(filterCriteria);
  const idsWaterActivities = filterAttributeMethodsWaterActivities.getIdsAttributesByObjectKeys(filterCriteria);
  const idsFacilities = facilitiesFilterAttributeMethods.getIdsAttributesByObjectKeys(filterCriteria);
  const idsServiceChild = childServiceFilterMethods.getIdsAttributesByObjectKeys(filterCriteria);
  const idsTypeDistanceLift = DistanceLiftAttribute.getIdsAttributesByObjectKeys(filterCriteria);
  const idsRestType = restTypeFilterMethods.getIdsAttributesByObjectKeys(filterCriteria);
  const idsRenovations = renovationFilterMethods.getIdsAttributesByObjectKeys(filterCriteria);

  if (returnType === 'part') {
    return {
      takenBeachType: idsBeachType,
      takenWaterActivities: idsWaterActivities,
      takenFacilities: idsFacilities,
      childServiceFilterIds: idsServiceChild,
      typeDistanceLift: idsTypeDistanceLift,
      restType: idsRestType[0] ?? 0,
      renovations: idsRenovations,
    };
  }

  return {
    takenHotelAttributes: idsAttributeHotel,
    renovations: idsRenovations,
    typeLines: idsBeachLine,
    takenBeachType: idsBeachType,
    typeFilterRating: idsRatingReviews[0] ?? 0,
    takenWaterActivities: idsWaterActivities,
    takenFacilities: idsFacilities,
    childServiceFilterIds: idsServiceChild,
    typeDistanceLift: idsTypeDistanceLift,
    restType: idsRestType[0] ?? 0,
  };
}

//функция принимает новый интерфейс фильтров и преобразовывает в старый интерфейс
export function getOldFilterCriteria(
  filterCriteriaNew: IFilterCriteriaModel,
  returnType: 'part' | 'all' = 'all',
): Partial<Record<OldFilterCriteria, boolean>> {
  if (!filterCriteriaNew) return {} as Record<OldFilterCriteria, boolean>;

  const valuesAttributeHotelOld = filterAttributeHotelMethodsByAll.getObjectKeysAttributesByIds(
    filterCriteriaNew?.takenHotelAttributes,
  );
  const valuesAttributeBeachLineOld = FilterAttributeBeachLineMethods.getObjectKeysAttributesByIds(
    filterCriteriaNew?.typeLines,
  );
  const valuesAttributeRatingReviewsOld = FilterAttributeRatingReviewsMethods.getObjectKeysAttributesByIds([
    filterCriteriaNew?.typeFilterRating,
  ]);
  const valuesAttributesBeachTypeOld = beachTypeFilterMethods.getObjectKeysAttributesByIds(
    filterCriteriaNew?.takenBeachType,
  );
  const valuesAttributesWaterActivitiesOld = filterAttributeMethodsWaterActivities.getObjectKeysAttributesByIds(
    filterCriteriaNew?.takenWaterActivities,
  );
  const valuesAttributesFacilitiesOld = facilitiesFilterAttributeMethods.getObjectKeysAttributesByIds(
    filterCriteriaNew?.takenFacilities,
  );
  const valuesAttributesChildServiceOld = childServiceFilterMethods.getObjectKeysAttributesByIds(
    filterCriteriaNew?.childServiceFilterIds,
  );
  const valuesAttributesRestTypeOld = restTypeFilterMethods.getObjectKeysAttributesByIds([filterCriteriaNew?.restType]);

  const valuesRenovations = renovationFilterMethods.getObjectKeysAttributesByIds(filterCriteriaNew.renovations);

  if (returnType === 'part') {
    return {
      ...valuesAttributesFacilitiesOld,
      ...valuesAttributesWaterActivitiesOld,
      ...valuesAttributesChildServiceOld,
      ...valuesAttributesBeachTypeOld,
      ...valuesAttributesRestTypeOld,
      ...valuesRenovations,
    };
  }

  return {
    ...valuesAttributeHotelOld,
    ...valuesAttributeBeachLineOld,
    ...valuesAttributeRatingReviewsOld,
    ...valuesAttributesBeachTypeOld,
    ...valuesRenovations,
    ...valuesAttributesWaterActivitiesOld,
    ...valuesAttributesFacilitiesOld,
    ...valuesAttributesChildServiceOld,
    ...valuesAttributesRestTypeOld,
  };
}

export const newFilterCriteriaNames: Array<keyof NewFilterCriteria> = [
  'takenHotelAttributes',
  'typeLines',
  'typeFilterRating',
  'takenBeachType',
  'takenWaterActivities',
  'takenFacilities',
  'childServiceFilterIds',
  'typeDistanceLift',
  'restType',
  'renovations',
];
